import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ResearchPushToggle from "./ResearchPushToggle";

type Mode = "summary" | "risks" | "house_view" | "all";

const MODE_TO_ACTION: Record<Exclude<Mode, "all">, string> = {
  summary: "exec_summary",
  risks: "risks",
  house_view: "house_view_draft",
};

interface Props {
  itemId: string;
  aiSummary?: string | null;
  aiRisks?: string | null;
  aiHouseView?: string | null;
  aiGeneratedAt?: string | null;
}

async function loadItemSources(itemId: string) {
  // Sources explicitly linked to this item
  const { data: linked } = await supabase
    .from("research_item_sources")
    .select("source_id, research_sources!inner(id, title, extracted_text, external_url)")
    .eq("item_id", itemId);
  const sources = (linked ?? [])
    .map((r: any) => r.research_sources)
    .filter((s: any) => s && (s.extracted_text || "").trim().length > 0)
    .map((s: any) => ({
      id: s.id,
      title: s.title ?? "(untitled)",
      text: (s.extracted_text ?? "").slice(0, 60000),
      url: s.external_url ?? null,
    }));
  if (sources.length > 0) return sources;
  // Fallback: use the item's own body so the assist actions can still run
  const { data: item } = await supabase
    .from("research_items")
    .select("id, title, body_markdown, summary")
    .eq("id", itemId)
    .maybeSingle();
  if (!item) return [];
  const text = (item.body_markdown ?? item.summary ?? "").trim();
  if (!text) return [];
  return [{ id: item.id, title: item.title ?? "(untitled)", text, url: null }];
}

export default function ResearchAIAssist({ itemId, aiSummary, aiRisks, aiHouseView, aiGeneratedAt }: Props) {
  const [loading, setLoading] = useState<Mode | null>(null);
  const qc = useQueryClient();
  // Existing results auto-load on mount (these props come straight from the cached
  // item). When something is already computed we lead with it and demote the
  // generate buttons to a "regenerate" row, so the analysis isn't hidden behind a
  // click each visit. Only when nothing is cached do we show the prominent run UI.
  const hasResults = Boolean(aiSummary || aiRisks || aiHouseView);
  const [showActions, setShowActions] = useState(false);

  const runOne = async (action: string, sources: any[]) => {
    const { data, error } = await supabase.functions.invoke("research-ai-assist", {
      body: { action, item_id: itemId, inputs: { sources } },
    });
    if (error) {
      // Try to surface the function's JSON error body (non-2xx wraps it in error.context)
      const ctx = (error as any)?.context;
      if (ctx && typeof ctx.json === "function") {
        try { const j = await ctx.json(); throw new Error(j.error || j.reason || error.message); } catch (e: any) { throw e; }
      }
      throw error;
    }
    if ((data as any)?.error) throw new Error((data as any).error);
    return data;
  };

  const run = async (mode: Mode) => {
    setLoading(mode);
    try {
      const sources = await loadItemSources(itemId);
      if (sources.length === 0) {
        toast.error("No source text or item body to analyse");
        return;
      }
      const actions = mode === "all" ? Object.values(MODE_TO_ACTION) : [MODE_TO_ACTION[mode]];
      for (const a of actions) await runOne(a, sources);
      toast.success("AI output generated");
      qc.invalidateQueries({ queryKey: ["research_item", itemId] });
    } catch (e: any) {
      toast.error(e.message || "AI request failed");
    } finally {
      setLoading(null);
    }
  };

  const actionButtons = (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => run("summary")}>
        {loading === "summary" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
        Summarise
      </Button>
      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => run("risks")}>
        {loading === "risks" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
        Extract risks
      </Button>
      <Button size="sm" variant="outline" disabled={!!loading} onClick={() => run("house_view")}>
        {loading === "house_view" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
        Draft house view
      </Button>
      <Button size="sm" disabled={!!loading} onClick={() => run("all")}>
        {loading === "all" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
        Run all
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Assist
          {aiGeneratedAt && (
            <span className="text-xs font-normal text-muted-foreground ml-auto">
              Last run {format(new Date(aiGeneratedAt), "dd/MM/yyyy HH:mm")}
            </span>
          )}
          <div className={aiGeneratedAt ? "" : "ml-auto"}><ResearchPushToggle /></div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasResults ? (
          <>
            {aiSummary && <Section title="Executive summary" body={aiSummary} />}
            {aiRisks && <Section title="Key risks" body={aiRisks} />}
            {aiHouseView && <Section title="House view draft" body={aiHouseView} />}

            {/* Results are cached and shown above; the generate buttons are tucked into
                a collapsed "Regenerate" row so a fresh run is one click away without
                hiding the analysis behind it each visit. */}
            <Collapsible open={showActions} onOpenChange={setShowActions} className="border-t pt-3">
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
                <ChevronDown className="h-3 w-3 transition-transform [&[data-state=open]]:rotate-180" />
                Regenerate
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                {actionButtons}
              </CollapsibleContent>
            </Collapsible>
          </>
        ) : (
          <>
            {actionButtons}
            <p className="text-sm text-muted-foreground">
              No AI output yet. Use the buttons above to generate.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title}</div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed">{body}</div>
    </div>
  );
}
